import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIdleTimer } from 'react-idle-timer';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    DialogContentText,
    Alert,
    INLINE_VARIANT,
    makeStyles,
    Snackbar
} from '@ellucian/react-design-system/core';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import { useIntl } from 'react-intl';
import { useHistory, matchPath } from 'react-router-dom';
import { spacing40 } from '@ellucian/react-design-system/core/styles/tokens';

const globalWarningState = { current: false };

const useStyles = makeStyles(() => ({
    dialogContentText: {
        textAlign: 'center',
        padding: spacing40,
        marginBottom: 0
    },
    dialogActions: {
        justifyContent: 'center',
        padding: spacing40
    },
    alert: {
        paddingLeft: '6.5rem'
    }
}));

const Timeout = props => {
    const intl = useIntl();
    const history = useHistory();
    const classes = useStyles();

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarVariant, setSnackbarVariant] = useState('info');

    const { sessionTimeout, warningThreshold, countdownDuration } = CONSTANTS.timeout;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(countdownDuration);
    const countdownIntervalRef = useRef(null);
    
    // Flag to track if dialog has been initially announced
    const dialogAnnounced = useRef(false);

    // Function to show snackbar
    const showSnackbar = (message, variant = 'info') => {
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
        setSnackbarOpen(true);
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const clearCountdownInterval = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        globalWarningState.current = false;
        dialogAnnounced.current = false;
    };

    // Clean up function to ensure dialog elements are properly removed
    const cleanupDialog = () => {
        // Clear any lingering timeouts
        clearCountdownInterval();
        // Reset announcement state
        dialogAnnounced.current = false;
    };

    const handleLogout = () => {
        // First set dialog to closed to remove from DOM
        setIsDialogOpen(false);
        // Clean up all dialog-related elements
        cleanupDialog();
        
        // Give the DOM time to fully update
        setTimeout(() => {
            // Set focus to the container to help screen reader reset its context
            const container = document.getElementById('timeoutContainer');
            if (container) {
                container.focus();
            }
            
            // Now show the snackbar with sufficient delay after dialog is gone
            setTimeout(() => {
                const message = intl.formatMessage({ id: 'timeoutSnackbarMessage' });
                showSnackbar(message, 'info');
                
                // Finally handle the navigation after snackbar has been announced
                setTimeout(() => {
                    props.forceTimeout();
                    handleTimeoutRedirect();
                }, 2000);
            }, 500);
        }, 300);
    };

    const handleContinue = () => {
        // First set dialog to closed to remove from DOM
        setIsDialogOpen(false);
        // Clean up all dialog-related elements
        cleanupDialog();
        
        props.handleReset();
        const message = intl.formatMessage({ id: 'timeoutExtendSnackbarMessage' });
        showSnackbar(message, 'success');
        resetIdleTimer();
    };
    
    const handleTimeoutRedirect = () => {
        const externalMatch = matchPath(history.location.pathname, {
            path: [routes.externalApplication, routes.applicationShare]
        });
        if (externalMatch) {
            history.push(routes.externalApplication);
            location.reload();
        }
        const retrievalMatch = matchPath(history.location.pathname, {
            path: [
                routes.applicationForm,
                routes.newApplication,
                routes.applicationFormSupplementalForm
            ]
        });
        const homeExactMatch = matchPath(history.location.pathname, {
            path: [routes.home],
            exact: true
        });
        if (retrievalMatch || homeExactMatch) {
            history.push(routes.retrieval);
        }
    };

    const onIdle = () => {
        const onRetrieval = matchPath(history.location.pathname, {
            path: [routes.retrieval, routes.retrievalSubmitted],
            exact: true
        });
        
        if (!onRetrieval) {
            if (globalWarningState.current) {
                return; // Prevent duplicate dialog and interval
            }
            globalWarningState.current = true;
            setIsDialogOpen(true);
            let countDown = countdownDuration;
            setTimeoutCountdown(countDown); // Start timeoutCountdown from 30 seconds

            // Reset announcement state for new dialog
            dialogAnnounced.current = false; // Will be set to true after initial render

            const interval = setInterval(() => {
                if (countDown > 0) {
                    countDown--;
                    setTimeoutCountdown(countDown);
                    // No additional announcements during countdown
                } else {
                    // Time is up - handle logout
                    handleLogout();
                }
            }, 1000);

            countdownIntervalRef.current = interval;
        } else {
            props.handleReset();
        }
    };

    // Mark dialog as announced when it opens and set focus on content
    useEffect(() => {
        if (isDialogOpen && !dialogAnnounced.current) {
            // Short delay to ensure the dialog is fully rendered before marking as announced
            const timer = setTimeout(() => {
                // Mark as announced
                dialogAnnounced.current = true;
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [isDialogOpen]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearCountdownInterval();
            // Ensure all timeouts are cleared
            const id = setTimeout(() => {}, 0);
            for (let i = 0; i < id; i++) {
                clearTimeout(i);
            }
        };
    }, []);

    // Additional effect to monitor dialog closing
    useEffect(() => {
        if (!isDialogOpen) {
            cleanupDialog();
        }
    }, [isDialogOpen]);

    const { reset: resetIdleTimer } = useIdleTimer({
        timeout: sessionTimeout - warningThreshold, // 30 seconds before timeout dialog appears
        onIdle,
        debounce: 250
    });

    return (
        <div id="timeoutContainer" tabIndex="-1">
            {isDialogOpen && (
                <>
                    <Dialog
                        id="timeoutDialog"
                        open={isDialogOpen}
                        showTitleBarClose={false}
                        disableEnforceFocus={false}
                        role="alertdialog"
                        aria-labelledby="timeoutDialogTitle"
                        aria-describedby="timeoutDialogContent"
                        spacingOptions={{ spacing: 'none', outerSpacing: false }}
                        onClose={() => {
                            setIsDialogOpen(false);
                            cleanupDialog();
                        }}
                    >
                        <DialogTitle id="timeoutDialogTitle">
                            <Alert
                                alertType="warning"
                                open={true}
                                userDismissable={false}
                                variant={INLINE_VARIANT}
                                text={intl.formatMessage({ id: 'timeoutDialogTitle' })}
                                className={classes.alert}
                            />
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText 
                                id="timeoutDialogContent" 
                                className={classes.dialogContentText}
                            >
                                {intl.formatMessage({ id: 'timeoutDialogWarningDescription' }, { timeoutCountdown })}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button
                                id="resetTimerButton"
                                color="primary"
                                onClick={handleContinue}
                                ref={button => {
                                    if (button && !dialogAnnounced.current) {
                                        button.focus();
                                    }
                                }
                                }
                            >
                                {intl.formatMessage({ id: 'timeoutDialogButton' })}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}

            {/* Snackbar for session timeout */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                variant={snackbarVariant}
                message={snackbarMessage}
            />
        </div>
    );
};

Timeout.propTypes = {
    isTimedout: PropTypes.bool.isRequired,
    forceTimeout: PropTypes.func.isRequired,
    timeoutTime: PropTypes.number,
    handleReset: PropTypes.func.isRequired
};

export default Timeout;
